import { Applicant } from '@/components/pages/Applicant/Applicant.types';
import { useNmiTemplatesQuery } from '@/components/shared/api/queries/nmiTemplates';
import { useMemo } from 'react';
import { REQUIREMENT_VALIDATORS } from './catalog';
import { ResolvedRequirement } from './types';

/**
 * Status badges + auto pre-selection are disabled for the v1 release. Flip to
 * true (or remove the gate) once Product is ready to ship the validators.
 * Keeping the validators + status types in place so re-enabling is a one-line
 * change here rather than a feature rebuild.
 */
const ENABLE_REQUIREMENT_STATUS_BADGES = false;

interface RequirementsCatalog {
    requirements: ResolvedRequirement[];
    isLoading: boolean;
    isError: boolean;
    refetch: () => void;
}

const EMPTY: ResolvedRequirement[] = [];

/**
 * Resolves the active NMI templates against an applicant, computing each
 * entry's status. Entries are sorted alphabetically by label so the order
 * matches what the drawer renders.
 */
export const useRequirementsCatalog = (applicant: Applicant | undefined): RequirementsCatalog => {
    const { data, isLoading, isError, refetch } = useNmiTemplatesQuery();

    const requirements = useMemo(() => {
        if (!data) return EMPTY;
        const resolved: ResolvedRequirement[] = data
            .filter((t) => t.isActive)
            .map((t) => {
                const validate = t.code ? REQUIREMENT_VALIDATORS[t.code] : undefined;
                return {
                    id: t.id,
                    label: t.label,
                    bulletText: t.body,
                    validate,
                    status:
                        ENABLE_REQUIREMENT_STATUS_BADGES && applicant && validate
                            ? validate(applicant)
                            : 'unknown',
                };
            });
        return resolved.sort((a, b) => a.label.localeCompare(b.label));
    }, [data, applicant]);

    return { requirements, isLoading, isError, refetch: () => void refetch() };
};
